import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Divider,
  AppBar,
  Toolbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Grid,
  Card,
  CardContent,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Remove,
  Delete,
  Payment,
  Note,
  Restaurant,
  ShoppingCart,
  AttachMoney,
  ArrowForward,
  ClearAll,
  CheckCircle,
  Person,
  EventSeat,
  Schedule
} from '@mui/icons-material';
import { useStore } from '../store/useStore';
import MenuDialog from './MenuDialog';
import PaymentDialog from './PaymentDialog';
import { useLanguage } from '../contexts/LanguageContext';

const TableDetail: React.FC = () => {
  const { tableId } = useParams<{ tableId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const isTabletLandscape = isTablet && isLandscape && !isMobile;
  const { t } = useLanguage();

  const {
    tables,
    updateItemQuantity,
    removeItemFromOrder,
    addNoteToTable,
    addNoteToOrder,
    moveTableForward,
    moveTableBackward,
    clearTable,
    updateReservation
  } = useStore();

  const [menuOpen, setMenuOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [noteType, setNoteType] = useState<'table' | 'order'>('table');
  const [noteText, setNoteText] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [reservationOpen, setReservationOpen] = useState(false);
  const [reservationTime, setReservationTime] = useState('');
  const [reservationName, setReservationName] = useState('');
  const [reservationPartySize, setReservationPartySize] = useState('');
  
  const formatTimeInput = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Limit to 4 digits
    const limitedDigits = digits.slice(0, 4);
    
    // Format with colon
    if (limitedDigits.length >= 3) {
      return limitedDigits.slice(0, -2) + ':' + limitedDigits.slice(-2);
    }
    return limitedDigits;
  };

  const table = tables.find(t => t.id === tableId);
  const activeOrder = table?.orders.find(o => o.status === 'pending');

  useEffect(() => {
    if (table?.notes) {
      setNoteText(table.notes);
    }
  }, [table?.notes]);

  if (!table) {
    return (
      <Container>
        <Typography variant="h6">{t('common.table')} not found</Typography>
        <Button
          onClick={() => navigate('/')}
          variant="contained"
          sx={{
            backgroundColor: '#f0f0f0',
            color: '#424242',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#e0e0e0'
            }
          }}
        >
          {t('navigation.backToTables')}
        </Button>
      </Container>
    );
  }

  const handleQuantityChange = (itemId: string, delta: number) => {
    if (!activeOrder) return;
    const item = activeOrder.items.find(i => i.id === itemId);
    if (item) {
      const newQuantity = item.quantity + delta;
      if (newQuantity > 0) {
        updateItemQuantity(activeOrder.id, itemId, newQuantity);
      } else {
        removeItemFromOrder(activeOrder.id, itemId);
      }
    }
  };

  const handleRemoveItem = (itemId: string) => {
    if (!activeOrder) return;
    removeItemFromOrder(activeOrder.id, itemId);
  };

  const handleOpenNotes = (type: 'table' | 'order', orderId?: string) => {
    setNoteType(type);
    if (orderId) {
      setSelectedOrderId(orderId);
      const order = table.orders.find(o => o.id === orderId);
      setNoteText(order?.notes || '');
    } else {
      setNoteText(table.notes || '');
    }
    setNotesOpen(true);
  };

  const handleSaveNote = () => {
    if (noteType === 'table') {
      addNoteToTable(table.id, noteText);
    } else if (selectedOrderId) {
      addNoteToOrder(selectedOrderId, noteText);
    }
    setNotesOpen(false);
    setNoteText('');
  };

  const handleOpenReservation = () => {
    if (table.reservation) {
      setReservationTime(table.reservation.time || '');
      setReservationName(table.reservation.name || '');
      setReservationPartySize(table.reservation.partySize?.toString() || '');
    } else {
      setReservationTime('');
      setReservationName('');
      setReservationPartySize('');
    }
    setReservationOpen(true);
  };

  const handleSaveReservation = () => {
    if (reservationTime || reservationName) {
      updateReservation(table.id, {
        time: reservationTime,
        name: reservationName,
        partySize: reservationPartySize ? parseInt(reservationPartySize) : undefined
      });
    } else {
      updateReservation(table.id, undefined);
    }
    setReservationOpen(false);
  };

  const handleClearReservation = () => {
    updateReservation(table.id, undefined);
    setReservationOpen(false);
  };

  const handleOpenPayment = () => {
    if (activeOrder && activeOrder.items.length > 0 && table.status === 'waiting-payment') {
      setPaymentOpen(true);
    }
  };

  const handleMoveForward = () => {
    if (table) {
      moveTableForward(table.id);
    }
  };

  const handleMoveBackward = () => {
    if (table?.status === 'free' || table?.status === 'awaiting-order') {
      setClearConfirmOpen(true);
    } else {
      moveTableBackward(table!.id);
    }
  };

  const handleClearTable = () => {
    if (table) {
      clearTable(table.id);
      setClearConfirmOpen(false);
    }
  };

  const getNextStatus = () => {
    if (!table) return '';
    switch (table.status) {
      case 'free':
        return table.orders.length > 0 ? t('tableStatus.awaitingOrder') : '';
      case 'awaiting-order':
        return t('tableStatus.readyForPayment');
      case 'waiting-payment':
        return '';
      default:
        return '';
    }
  };

  const getPreviousStatus = () => {
    if (!table) return '';
    switch (table.status) {
      case 'waiting-payment':
        return t('tableStatus.awaitingOrder');
      case 'awaiting-order':
        return t('tableActions.clearTable');
      case 'free':
        return table.orders.length > 0 ? t('tableActions.clearTable') : '';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh', pb: 10 }}>
      <AppBar position="sticky" sx={{
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        borderBottom: 'none'
      }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            edge="start"
            sx={{ color: '#1a1a1a', mr: 1 }}
            onClick={() => navigate('/')}
            size={isMobile ? "medium" : "large"}
          >
            <ArrowBack />
          </IconButton>
          <Restaurant sx={{ mr: 1, display: { xs: 'none', sm: 'block' }, color: '#1a1a1a' }} />
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            component="div" 
            sx={{ flexGrow: 1, fontWeight: 300, letterSpacing: '-0.01em', color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            Table {table.number}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              <Person sx={{ fontSize: isMobile ? 18 : 22 }} />
              <span style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>{table.seats} {t('common.seats')}</span>
            </Box>
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth={isTabletLandscape ? "xl" : "lg"} sx={{ mt: 2, px: { xs: 1, sm: 2 } }}>
        {/* Layout Grid for Landscape Mode */}
        <Box sx={{ 
          display: isTabletLandscape ? 'grid' : 'block',
          gridTemplateColumns: isTabletLandscape ? '400px 1fr' : '1fr',
          gap: isTabletLandscape ? 2 : 0,
          alignItems: 'start'
        }}>
          {/* Left Column - Status Control */}
          <Box>
            {/* Table Status Control Card */}
            <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 2
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between'
              }}>
                <Box>
                  <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
                    Status Control
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={table.status === 'free' ? 'Available' : 
                              table.status === 'awaiting-order' ? t('tableStatus.awaitingOrder') : t('tableStatus.readyForPayment')}
                      color={table.status === 'free' ? 'success' : 
                             table.status === 'awaiting-order' ? 'warning' : 'error'}
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                      size={isMobile ? "small" : "medium"}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {getPreviousStatus() && (
                      <Button
                        size={isMobile ? "medium" : "large"}
                        variant="contained"
                        startIcon={getPreviousStatus() === t('tableActions.clearTable') ? <ClearAll /> : <ArrowBack />}
                        onClick={handleMoveBackward}
                        sx={{ 
                          minHeight: { xs: 40, sm: 48 },
                          backgroundColor: getPreviousStatus() === t('tableActions.clearTable') ? '#ef5350' : '#f0f0f0',
                          color: getPreviousStatus() === t('tableActions.clearTable') ? 'white' : '#424242',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: getPreviousStatus() === t('tableActions.clearTable') ? '#d32f2f' : '#e0e0e0'
                          }
                        }}
                      >
                        {getPreviousStatus()}
                      </Button>
                    )}
                    {getNextStatus() && (
                      <Button
                        size={isMobile ? "medium" : "large"}
                        variant="contained"
                        endIcon={<ArrowForward />}
                        onClick={handleMoveForward}
                        sx={{
                          backgroundColor: '#b5cbb3',
                          color: '#1a1a1a',
                          minHeight: { xs: 40, sm: 48 },
                          '&:hover': {
                            backgroundColor: '#a3b9a1'
                          }
                        }}
                      >
                        {getNextStatus()}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                justifyContent: 'flex-end',
                flexWrap: 'wrap'
              }}>
                <Button
                  variant="contained"
                  startIcon={<Note sx={{ color: 'white' }} />}
                  onClick={() => handleOpenNotes('table')}
                  size={isMobile ? "medium" : "large"}
                  sx={{ 
                    minHeight: { xs: 40, sm: 48 },
                    backgroundColor: '#424242',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#303030'
                    }
                  }}
                >
                  {t('tableDetails.tableNotes')}
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<EventSeat sx={{ color: 'white' }} />}
                  onClick={handleOpenReservation}
                  size={isMobile ? "medium" : "large"}
                  sx={{ 
                    minHeight: { xs: 40, sm: 48 },
                    backgroundColor: table.reservation ? '#ff9800' : '#757575',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: table.reservation ? '#f57c00' : '#616161'
                    }
                  }}
                >
                  {table.reservation ? `${t('tableDetails.reservationDetails')} ${table.reservation.time}` : t('tableActions.addReservation')}
                </Button>
              </Box>
            </Box>
          </CardContent>
            </Card>
          </Box>

          {/* Right Column - Current Order */}
          <Box>
            {/* Current Order */}
            {activeOrder && (
              <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ShoppingCart sx={{ mr: 1 }} />
              {t('tableDetails.currentOrder')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {activeOrder.items.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No items in order yet
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setMenuOpen(true)}
                  size="large"
                  sx={{ 
                    mt: 2,
                    minHeight: 48,
                    backgroundColor: '#b5cbb3',
                    color: '#1a1a1a',
                    '&:hover': {
                      backgroundColor: '#a3b9a1'
                    }
                  }}
                >
                  {t('tableActions.addItems')}
                </Button>
              </Box>
            ) : (
              <>
                <List sx={{ pb: 0 }}>
                  {activeOrder.items.map((item) => (
                    <ListItem
                      key={item.id}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: '#f9f9f9',
                        p: { xs: 1, sm: 2 },
                        '&:hover': { bgcolor: '#f0f0f0' }
                      }}
                      secondaryAction={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                          <IconButton
                            size={isMobile ? "medium" : "large"}
                            onClick={() => handleQuantityChange(item.id, -1)}
                            sx={{ 
                              bgcolor: 'white',
                              border: '1px solid #e0e0e0',
                              '&:hover': { bgcolor: '#f5f5f5' }
                            }}
                          >
                            <Remove fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                          <Typography sx={{ 
                            mx: 1, 
                            minWidth: 30, 
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: { xs: '1rem', sm: '1.1rem' }
                          }}>
                            {item.quantity}
                          </Typography>
                          <IconButton
                            size={isMobile ? "medium" : "large"}
                            onClick={() => handleQuantityChange(item.id, 1)}
                            sx={{ 
                              bgcolor: 'white',
                              border: '1px solid #e0e0e0',
                              '&:hover': { bgcolor: '#f5f5f5' }
                            }}
                          >
                            <Add fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                          <Typography sx={{ 
                            ml: { xs: 1, sm: 2 }, 
                            minWidth: 60, 
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', sm: '1rem' }
                          }}>
                            €{(item.price * item.quantity).toFixed(2)}
                          </Typography>
                          <IconButton
                            size={isMobile ? "small" : "medium"}
                            color="error"
                            onClick={() => handleRemoveItem(item.id)}
                            sx={{ ml: { xs: 0.5, sm: 1 } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography variant={isMobile ? "body1" : "h6"}>
                            {item.translationKey ? t(`menu.items.${item.translationKey}`) : item.name}
                          </Typography>
                        }
                        secondary={
                          <Box component="span">
                            <Typography variant={isMobile ? "caption" : "body2"} component="span" display="block">
                              €{item.price.toFixed(2)} each
                            </Typography>
                            {item.notes && (
                              <Typography variant="caption" component="span" display="block" sx={{ mt: 0.5 }}>
                                Note: {item.notes}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 2
                }}>
                  <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
                    {t('common.total')}: €{activeOrder.total.toFixed(2)}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Payment />}
                    onClick={handleOpenPayment}
                    size="large"
                    fullWidth={isMobile}
                    disabled={table.status !== 'waiting-payment'}
                    sx={{
                      backgroundColor: table.status === 'waiting-payment' ? '#CE2B37' : '#9e9e9e',
                      minHeight: { xs: 48, sm: 56 },
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      '&:hover': {
                        backgroundColor: table.status === 'waiting-payment' ? '#a62329' : '#757575'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#9e9e9e'
                      }
                    }}
                  >
                    {table.status === 'waiting-payment' ? t('tableActions.processPayment') :
                     table.status === 'awaiting-order' ? t('tableStatus.awaitingOrder') :
                     t('tableActions.processPayment')}
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        )}

        {/* Past Orders */}
        {table.orders.filter(o => o.status !== 'pending').length > 0 && (
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
              Order History
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              {table.orders.filter(o => o.status !== 'pending').map((order) => (
                <ListItem key={order.id}>
                  <ListItemText
                    primary={`Order ${order.id.slice(0, 8)}`}
                    secondary={`${order.items.length} items - €${order.total.toFixed(2)} - ${order.status}`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
          </Box>
        </Box>
      </Container>

      {/* Floating Action Button for Adding Items */}
      <Fab
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          width: { xs: 64, sm: 72 },
          height: { xs: 64, sm: 72 },
          backgroundColor: '#b5cbb3',
          color: '#1a1a1a',
          '&:hover': {
            backgroundColor: '#a3b9a1'
          }
        }}
        onClick={() => setMenuOpen(true)}
      >
        <Add sx={{ fontSize: { xs: 28, sm: 32 } }} />
      </Fab>

      {/* Menu Dialog */}
      <MenuDialog
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        tableId={table.id}
      />

      {/* Payment Dialog */}
      <PaymentDialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        order={activeOrder}
        tableId={table.id}
      />

      {/* Notes Dialog */}
      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {noteType === 'table' ? t('tableDetails.tableNotes') : t('tableDetails.orderNotes')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={`Add notes for ${noteType === 'table' ? 'this table' : 'this order'}...`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setNotesOpen(false)} 
            size="large"
            variant="contained"
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#424242',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#d0d0d0'
              }
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSaveNote} 
            variant="contained" 
            size="large"
            sx={{ minWidth: 100 }}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Table Confirmation Dialog */}
      <Dialog open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
          <ClearAll sx={{ mr: 1 }} />
          {t('tableActions.clearTable')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('confirmations.clearTableConfirm')} {t('confirmations.clearTableWarning')}
          </Typography>
          <List dense sx={{ mt: 1 }}>
            <ListItem>• {t('confirmations.removeAllOrders')}</ListItem>
            <ListItem>• {t('common.clear')} {t('common.notes').toLowerCase()}</ListItem>
            <ListItem>• {t('confirmations.resetTableStatus')}</ListItem>
          </List>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('confirmations.actionCannotBeUndone')}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setClearConfirmOpen(false)} 
            size="large"
            variant="contained"
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#424242',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#d0d0d0'
              }
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleClearTable} 
            variant="contained" 
            color="error"
            startIcon={<ClearAll />}
            size="large"
            sx={{ minWidth: 120 }}
          >
            {t('tableActions.clearTable')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reservation Dialog */}
      <Dialog open={reservationOpen} onClose={() => setReservationOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventSeat />
          {t('tableDetails.reservationDetails')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label={t('tableDetails.reservationTime')}
              placeholder={t('tableDetails.reservationTimePlaceholder')}
              value={reservationTime}
              onChange={(e) => {
                const formatted = formatTimeInput(e.target.value);
                setReservationTime(formatted);
              }}
              fullWidth
              inputMode="numeric"
              helperText="Enter 3-4 digits (e.g., 730 for 7:30 or 1930 for 19:30)"
              InputProps={{
                startAdornment: <Schedule sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <TextField
              label={t('tableDetails.reservationName')}
              placeholder={t('tableDetails.reservationNamePlaceholder')}
              value={reservationName}
              onChange={(e) => setReservationName(e.target.value)}
              fullWidth
            />
            <TextField
              label={t('tableDetails.partySize')}
              placeholder={t('tableDetails.partySizePlaceholder')}
              type="number"
              value={reservationPartySize}
              onChange={(e) => setReservationPartySize(e.target.value)}
              fullWidth
              inputProps={{ min: 1, max: table.seats }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {table.reservation && (
            <Button 
              onClick={handleClearReservation}
              color="error"
              sx={{ mr: 'auto' }}
            >
              {t('tableActions.removeReservation')}
            </Button>
          )}
          <Button 
            onClick={() => setReservationOpen(false)} 
            size="large"
            variant="contained"
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#424242',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#d0d0d0'
              }
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSaveReservation} 
            variant="contained" 
            size="large"
            sx={{ 
              backgroundColor: '#ff9800',
              '&:hover': {
                backgroundColor: '#f57c00'
              },
              minWidth: 100 
            }}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TableDetail;